import { Check, Search } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { cn } from "@/utils/cn";

type WantedShiftSearchProps = {
  readonly shifts: ShiftReturnType;
  readonly selectedShiftId: string;
  readonly disabled: boolean;
  readonly onShiftChange: (shiftId: string) => void;
};

function getShiftRequestTypeLabel(shift: ShiftReturnType[number]) {
  return shift.type?.trim() || "근무";
}

function getHighlightedText(value: string, query: string): ReactNode {
  if (!query) return value;

  const start = value.toLocaleLowerCase("ko-KR").indexOf(query);
  if (start < 0) return value;

  const end = start + query.length;
  return (
    <>
      {value.slice(0, start)}
      <mark className="rounded-sm bg-sig/15 px-0.5 font-semibold text-inherit">
        {value.slice(start, end)}
      </mark>
      {value.slice(end)}
    </>
  );
}

export function WantedShiftSearch({
  shifts,
  selectedShiftId,
  disabled,
  onShiftChange,
}: WantedShiftSearchProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const normalizedQuery = query.trim().toLocaleLowerCase("ko-KR");
  const filteredShifts = useMemo(
    () =>
      shifts.filter(
        (shift) =>
          !normalizedQuery ||
          shift.shift_id.toLocaleLowerCase("ko-KR").includes(normalizedQuery) ||
          shift.name.toLocaleLowerCase("ko-KR").includes(normalizedQuery)
      ),
    [normalizedQuery, shifts]
  );
  const activeShift = filteredShifts[activeIndex];

  const chooseShift = (shiftId: string) => {
    if (disabled) return;

    onShiftChange(shiftId);
    setQuery("");
    setActiveIndex(-1);
  };

  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">근무코드 선택</legend>
      <div className="group relative">
        <label htmlFor="wanted-shift-search" className="sr-only">
          근무코드 검색
        </label>
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-sub transition-colors group-focus-within:text-text-main2"
        />
        <input
          id="wanted-shift-search"
          name="wanted-shift-search"
          type="search"
          value={query}
          autoComplete="off"
          aria-controls="wanted-shift-options"
          placeholder="코드 또는 이름"
          className="h-11 w-full rounded-full border border-transparent bg-zinc-100/90 py-2 pl-9 pr-3 text-sm text-text-main2 outline-none placeholder:text-xs placeholder:text-text-sub/70 transition-[background-color,box-shadow] hover:bg-zinc-100 focus:bg-background focus-visible:ring-1 focus-visible:ring-text-sub/40 focus-visible:ring-offset-0 disabled:cursor-not-allowed"
          disabled={disabled}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(-1);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((index) =>
                Math.min(index + 1, Math.max(filteredShifts.length - 1, 0))
              );
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            }
            if (event.key === "Enter" && activeShift) {
              event.preventDefault();
              chooseShift(activeShift.shift_id);
            }
          }}
        />
      </div>
      <ul
        id="wanted-shift-options"
        aria-label="선택 가능한 근무코드"
        className="mt-3 grid grid-cols-1 gap-1 pr-1"
      >
        {filteredShifts.length > 0 ? (
          filteredShifts.map((shift, index) => (
            <li key={shift.shift_id}>
              <button
                type="button"
                aria-pressed={selectedShiftId === shift.shift_id}
                className={cn(
                  "flex min-h-10 w-full items-center gap-2 rounded-md px-2 text-left text-sm transition-[background-color,box-shadow] hover:bg-light-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-inset",
                  index === activeIndex || selectedShiftId === shift.shift_id
                    ? "bg-sky-50"
                    : ""
                )}
                aria-label={`근무코드 ${shift.shift_id} ${shift.name}`}
                onClick={() => chooseShift(shift.shift_id)}
                disabled={disabled}
              >
                <span
                  translate="no"
                  className="flex size-6 shrink-0 items-center justify-center rounded text-xs font-bold"
                  style={{
                    backgroundColor: shift.color,
                    color: getReadableShiftCodeTextColor(shift.color),
                  }}
                >
                  {getHighlightedText(shift.shift_id, normalizedQuery)}
                </span>
                <span
                  title={shift.name}
                  className="max-w-36 shrink-0 truncate text-text-main2"
                >
                  {getHighlightedText(shift.name, normalizedQuery)}
                </span>
                <span className="shrink-0 whitespace-nowrap text-[11px] font-medium text-text-sub/70">
                  · {getShiftRequestTypeLabel(shift)}
                </span>
                {selectedShiftId === shift.shift_id && (
                  <Check
                    aria-label="선택됨"
                    className="ml-auto size-4 shrink-0 text-sig"
                  />
                )}
              </button>
            </li>
          ))
        ) : (
          <li className="col-span-full px-3 py-3 text-sm text-text-sub">
            일치하는 근무코드가 없습니다.
          </li>
        )}
      </ul>
    </fieldset>
  );
}
